/* The search as a flowchart: boxes down the page, arrows between them, lines
 * pointing BACK where the engine backtracked, and the text it consumed on the
 * way.
 *
 * **The owner asked for this in as many words, 2026-09-10**, then twice more
 * because the first two answers were the wrong picture. The waterfall it
 * replaced said what the search did over the whole subject and nothing about
 * the pattern; the first flowchart showed one attempt and stopped at the first
 * match. This is the whole run: every attempt that found something drawn in
 * full, the rest collapsed into a band that says what they cost.
 *
 * **A BOX IS A PART OF THE PATTERN, NOT A MOMENT IN TIME, AND THAT IS WHAT
 * MAKES THE BACK EDGES REAL.** Keyed by time, every step is a new box and
 * nothing ever points back: a backtrack is just the next box down. Keyed by the
 * pattern item AND the position, a backtrack lands on a state never visited
 * before, so it is still a new box - which is what the first draft did, and it
 * drew zero back edges on a pattern that plainly backtracks. Keyed by the
 * pattern ITEM, within one attempt, the boxes are the parts of the regular
 * expression in the order they are written and a return is an arrow that points
 * at a box already on the page.
 *
 * **THE BOXES CARRY THE READER'S OWN TEXT.** This replaced a step list, and a
 * picture that dropped what the list said would have been a worse tool drawn
 * better: each box says where the engine was and what that part of the pattern
 * consumed there, which is the thing a reader is actually trying to find out.
 *
 * **PURE, like `diagram.js`**: arithmetic in, one string out, so the geometry is
 * testable without a browser. This repo cannot drive a browser in its suite.
 */

import { groupNameFor } from "./diagram.js";

/* **EVERY FACE IN THIS PICTURE IS THE MONO STACK, AND THAT IS WHY IT CAN BE
   MEASURED.** An advance width of exactly 0.6em is what makes the layout a pure
   function of the text - `diagram.js` rests on the same fact. The first version
   measured the headings and the bands at a SANS estimate while the stylesheet
   was giving them mono, so every one of them was a fifth wider than the canvas
   computed for it, and an SVG clips to its own viewport: the owner reported the
   bands cut off mid word. A face and a size, never a guess. */
const MONO = (size) => size * 0.6;

const CHAR = MONO(12);    // a box's title
const SMALL = MONO(11);   // a box's detail lines, an attempt's heading, a band
const TINY = MONO(10);    // an edge label
const BIG = MONO(13);     // what an attempt came to

const LINE = 14;          // a line of text inside a box
const PAD_X = 10;
const PAD_Y = 8;
const GAP = 24;           // vertical room between boxes, for the arrow
const LANE = 14;          // how far apart two nested routed edges sit
const CHANNEL = 18;       // between a box and the nearest lane
const HEAD = 20;          // an attempt's heading, above its boxes
const TAIL = 22;          // what the attempt came to, below them
const JOIN = 22;          // between one attempt and the next
const MEASURE = 64;       // characters a band wraps at, at its narrowest
const MAX_BOXES = 140;    // beyond this a flowchart stops being one
const MAX_TEXT = 24;      // of the reader's text, quoted inside a box
const MAX_VISITS = 3;     // visits listed in full before they are counted

/**
 * Reduce the steps to one summary per attempt.
 *
 * **`from` is what separates attempts** - it is where the engine began this
 * try - and within one, a position behind the previous step is a real backtrack
 * rather than the bump along between attempts.
 */
export function attemptsOf(steps, matches) {
  const attempts = [];

  steps.forEach((step, index) => {
    let attempt = attempts[attempts.length - 1];

    if (!attempt || attempt.from !== step.from) {
      attempt = {
        from: step.from,
        furthest: step.at,
        firstStep: index,
        lastStep: index,
        matched: null,
      };
      attempts.push(attempt);
    }

    attempt.furthest = Math.max(attempt.furthest, step.at);
    attempt.lastStep = index;
  });

  // Attribute matches by STEP INDEX rather than by offset: `\K` moves a match's
  // reported start away from where its attempt began.
  for (const match of matches) {
    const attempt = attempts.find(
      (a) => match.afterStep > a.firstStep && match.afterStep <= a.lastStep + 1);

    if (attempt) {
      attempt.matched = match;
    }
  }

  return attempts;
}

/**
 * One attempt's steps as boxes and arrows.
 *
 * Returns `{ nodes, edges }`. A node carries every visit the engine made to
 * that part of the pattern and what it consumed there; an edge carries how many
 * times that move was made, because a loop taken forty times and one taken once
 * are different facts about a pattern.
 */
export function statesOf(steps, pattern, subject, match = null) {
  const byItem = new Map();

  steps.forEach((step, index) => {
    const key = `${step.item.start}:${step.item.end}`;

    if (!byItem.has(key)) {
      byItem.set(key, { item: step.item, visits: [] });
    }

    byItem.get(key).visits.push({ at: step.at, took: tookAt(steps, index, subject, match) });
  });

  // **In PATTERN order, which is what makes it readable as a flowchart.** The
  // reader already knows the shape of their own regular expression; showing the
  // parts in the order they wrote them means the arrows are the only new
  // information.
  const nodes = [...byItem.values()].sort((a, b) => a.item.start - b.item.start);

  const index = new Map();
  nodes.forEach((node, i) => index.set(`${node.item.start}:${node.item.end}`, i));

  for (const node of nodes) {
    const fragment = pattern.slice(node.item.start, node.item.end);

    node.fragment = fragment;
    node.title = nameOf(fragment);
    node.lines = [node.title, ...visitLines(node.visits)];
    node.took = node.visits.filter((v) => v.took.length > 0)
      .map((v) => ({ start: v.at, end: v.at + v.took.length }));
  }

  const moves = new Map();

  for (let i = 1; i < steps.length; i++) {
    const from = index.get(`${steps[i - 1].item.start}:${steps[i - 1].item.end}`);
    const to = index.get(`${steps[i].item.start}:${steps[i].item.end}`);
    const key = `${from}>${to}`;

    if (!moves.has(key)) {
      moves.set(key, { from, to, count: 0, gaveBack: false });
    }

    const move = moves.get(key);
    move.count += 1;

    // The engine giving text back is what a reader means by backtracking, and
    // it is not the same as the arrow pointing at an earlier part of the
    // pattern - a loop does that too. Both are recorded; the drawing shows the
    // direction and the label says whether text was returned.
    if (steps[i].at < steps[i - 1].at) {
      move.gaveBack = true;
    }
  }

  // **A BOX THAT LOOPS ON ITSELF IS THE COMMONEST BACKTRACK THERE IS, and it
  // was invisible.** `\.` after `[A-Za-z0-9.-]+` is tried at 16, then 15, then
  // 14, then 13, because the repeat in front of it gives a character back each
  // time - the same part of the pattern, over and over, walking backwards. A
  // move from a box to itself was skipped as uninteresting, so the picture drew
  // no arrow at all for the one thing the owner asked to see.
  const edges = [...moves.values()].map((move) => ({
    ...move,
    self: move.to === move.from,
    back: move.to <= move.from,
    label: move.gaveBack
      ? move.count > 1 ? `gave text back, ${move.count} times` : "gave text back"
      : move.count > 1 ? `${move.count} times` : "",
  }));

  return { nodes, edges };
}

/**
 * The whole search, as one flowchart. Returns
 * `{ svg, width, height, sections, drawn, boxes, truncated }`.
 *
 * **It does not stop at the first match**, which is what the owner reported
 * about the version before this one. Every attempt that found something is
 * drawn in full; the failures between them collapse into a band that says how
 * many there were and how far they got, because forty near-identical pictures
 * of nothing happening is the unreadability this replaced, drawn larger.
 *
 * **When nothing matched at all, the attempt that got FURTHEST is drawn**, and
 * that is the case this has to get right: "why did my pattern not match" is the
 * question a reader has when there is nothing to show them.
 */
export function drawSearch(walked, pattern, subject) {
  const attempts = attemptsOf(walked.steps, walked.matches);
  const sections = plan(attempts, walked, pattern, subject);
  const blocks = sections.filter((s) => s.kind === "attempt");

  if (blocks.length === 0) {
    return { svg: "", width: 0, height: 0, sections, drawn: 0, boxes: 0, truncated: false };
  }

  // One box width for the whole picture, or the blocks read as separate
  // drawings that happen to be stacked.
  const boxWidth = PAD_X * 2 + CHAR * Math.max(
    ...blocks.flatMap((b) => b.nodes.flatMap((n) => n.lines.map((l) => l.length))));

  const widestLabel = (list) =>
    list.reduce((w, e) => Math.max(w, e.label.length * TINY), 0);

  // A routed edge's label sits right against its own lane, so the room the left
  // needs is its outermost lane plus the widest label on it.
  const left = Math.max(...blocks.map((b) =>
    b.backDepth === 0
      ? PAD_X
      : PAD_X + widestLabel(b.backEdges) + 4 + (b.backDepth - 1) * LANE + CHANNEL));

  const rightOfBoxes = left + boxWidth;

  // **A BAND WRAPS, IT DOES NOT RUN OFF THE EDGE.** A sentence about sixteen
  // attempts is longer than any box above it, and SVG text neither wraps by
  // itself nor spills past the viewport - it is simply cut. So it is broken
  // into lines here, at the wider of the drawing and a comfortable measure, and
  // the canvas is then sized from the lines rather than from the sentence.
  const columns = Math.max(MEASURE, Math.floor(boxWidth / SMALL));

  for (const section of sections) {
    if (section.kind === "skipped") {
      section.lines = wrap(section.text, columns);
      section.height = PAD_Y * 2 + section.lines.length * LINE;
    }
  }

  // **The prose is as wide as the drawing, and it is easy to forget.**
  const prose = Math.max(...sections.map((s) =>
    s.kind === "skipped"
      ? Math.max(...s.lines.map((l) => l.length)) * SMALL
      : Math.max(s.heading.length * SMALL, s.outcome.length * BIG)));

  const width = PAD_X + Math.max(rightOfBoxes, left + prose, ...blocks.map((b) => Math.max(
    left + boxWidth / 2 + CHANNEL + widestLabel(b.straightEdges),
    b.jumpDepth === 0
      ? 0
      : rightOfBoxes + CHANNEL + (b.jumpDepth - 1) * LANE + 4 + widestLabel(b.jumpEdges))));

  const centre = left + boxWidth / 2;
  const parts = [`<defs>
    <marker id="rx-f-arrow-edge" viewBox="0 0 8 8" refX="6.5" refY="4" markerWidth="8" markerHeight="8"
            orient="auto-start-reverse"><path d="M 1.5 1 L 6.5 4 L 1.5 7" class="rx-f-head rx-f-head-edge"/></marker>
    <marker id="rx-f-arrow-back" viewBox="0 0 8 8" refX="6.5" refY="4" markerWidth="8" markerHeight="8"
            orient="auto-start-reverse"><path d="M 1.5 1 L 6.5 4 L 1.5 7" class="rx-f-head rx-f-head-back"/></marker>
  </defs>`];

  let y = PAD_Y;

  sections.forEach((section, index) => {
    if (section.kind === "skipped") {
      parts.push(`<line class="rx-f-join" x1="${round(centre)}" y1="${round(y)}" ` +
        `x2="${round(centre)}" y2="${round(y + section.height)}"/>`);

      section.lines.forEach((text, line) => {
        parts.push(`<text class="rx-f-band" x="${round(left)}" ` +
          `y="${round(y + PAD_Y + 4 + line * LINE)}">${escape(text)}</text>`);
      });

      y += section.height;
      return;
    }

    drawBlock(section, y, left, boxWidth, parts);
    y += section.height;

    if (index < sections.length - 1 && sections[index + 1].kind === "attempt") {
      parts.push(`<line class="rx-f-join" x1="${round(centre)}" y1="${round(y)}" ` +
        `x2="${round(centre)}" y2="${round(y + JOIN)}"/>`);
      y += JOIN;
    }
  });

  const height = y + PAD_Y;
  const boxes = blocks.reduce((n, b) => n + b.nodes.length, 0);

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${round(width)}" height="${round(height)}" ` +
    `viewBox="0 0 ${round(width)} ${round(height)}" class="rx-flow" role="img" ` +
    `aria-label="${escape(summarise(attempts, walked.matches, blocks))}">` +
    parts.join("") + `</svg>`;

  return {
    svg,
    width,
    height,
    sections,
    drawn: blocks.length,
    boxes,
    truncated: sections.some((s) => s.truncated),
  };
}

/**
 * Which attempts get drawn, and what the rest are collapsed into.
 *
 * **Every attempt that matched**, because stopping at the first is the defect
 * this replaced. When none did, the one that got furthest, because that is the
 * only picture that answers "why did it not match".
 */
function plan(attempts, walked, pattern, subject) {
  const full = new Set();

  attempts.forEach((attempt, index) => {
    if (attempt.matched) {
      full.add(index);
    }
  });

  if (full.size === 0 && attempts.length > 0) {
    let best = 0;

    attempts.forEach((attempt, index) => {
      if (attempt.furthest - attempt.from > attempts[best].furthest - attempts[best].from) {
        best = index;
      }
    });

    full.add(best);
  }

  const sections = [];
  let skipped = [];
  let boxes = 0;
  let truncated = false;

  const flush = () => {
    if (skipped.length > 0) {
      sections.push({ kind: "skipped", text: bandText(skipped), attempts: skipped });
      skipped = [];
    }
  };

  attempts.forEach((attempt, index) => {
    if (!full.has(index) || truncated) {
      skipped.push({ ...attempt, index });
      return;
    }

    const steps = walked.steps.slice(attempt.firstStep, attempt.lastStep + 1);
    const { nodes, edges } = statesOf(steps, pattern, subject, attempt.matched);

    // **A cap on the whole picture, not on one attempt.** A subject with fifty
    // matches would otherwise draw fifty flowcharts, which is the unreadability
    // this replaced with more ink behind it.
    if (boxes + nodes.length > MAX_BOXES) {
      truncated = true;
      skipped.push({ ...attempt, index });
      return;
    }

    boxes += nodes.length;
    flush();
    sections.push(block(attempt, index, attempts.length, nodes, edges, subject));
  });

  flush();

  if (truncated && sections.length > 0) {
    sections[sections.length - 1].truncated = true;
  }

  return sections;
}

/** One attempt's geometry, measured but not yet placed. */
function block(attempt, index, total, nodes, edges, subject) {
  const backEdges = edges.filter((e) => e.back);
  const jumpEdges = edges.filter((e) => !e.back && e.to !== e.from + 1);
  const straightEdges = edges.filter((e) => !e.back && e.to === e.from + 1);


  const backLanes = laneFor(backEdges);
  const jumpLanes = laneFor(jumpEdges);

  const heights = nodes.map((n) => PAD_Y * 2 + n.lines.length * LINE);

  return {
    kind: "attempt",
    index,
    attempt,
    nodes,
    edges,
    backEdges,
    jumpEdges,
    straightEdges,
    backLanes,
    jumpLanes,
    backDepth: depthOf(backLanes),
    jumpDepth: depthOf(jumpLanes),
    heights,
    truncated: false,
    heading: `Attempt ${index + 1} of ${total}, from position ${attempt.from}`,
    outcome: attempt.matched
      ? `matched ${quote(subject.slice(attempt.matched.start, attempt.matched.end))}`
      : "no match from here",
    matched: Boolean(attempt.matched),
    height: HEAD + heights.reduce((a, b) => a + b, 0) + nodes.length * GAP + TAIL,
  };
}

/** Place one attempt's boxes and arrows, with its top edge at `y`. */
function drawBlock(section, y, left, boxWidth, parts) {
  const { nodes, heights, backEdges, jumpEdges, straightEdges, backLanes, jumpLanes, backDepth } = section;

  const centre = left + boxWidth / 2;
  const rightOfBoxes = left + boxWidth;

  const tops = [];
  let cursor = y + HEAD;

  heights.forEach((h) => {
    tops.push(cursor);
    cursor += h + GAP;
  });

  const top = (i) => tops[i];
  const bottom = (i) => tops[i] + heights[i];
  const middle = (i) => tops[i] + heights[i] / 2;

  parts.push(`<text class="${section.matched ? "rx-f-head-won" : "rx-f-heading"}" ` +
    `x="${round(left)}" y="${round(y + 12)}">${escape(section.heading)}</text>`);

  // Straight arrows first, so a box always sits on top of the line into it.
  for (const edge of straightEdges) {
    parts.push(`<line class="rx-f-edge" x1="${round(centre)}" y1="${round(bottom(edge.from))}" ` +
      `x2="${round(centre)}" y2="${round(top(edge.to))}" marker-end="url(#rx-f-arrow-edge)"/>`);

    if (edge.label) {
      parts.push(`<text class="rx-f-label" x="${round(centre + CHANNEL)}" ` +
        `y="${round(bottom(edge.from) + GAP / 2 + 3)}">${escape(edge.label)}</text>`);
    }
  }

  // Then the routed ones, out where they cannot cross a box.
  for (const edge of backEdges) {
    const x = left - CHANNEL - backLanes.get(edge) * LANE;
    const line = edge.gaveBack || !edge.self ? "rx-f-back" : "rx-f-edge";

    parts.push(edge.self
      ? loop(x, left, middle(edge.from), line)
      : route(x, left, middle(edge.from), middle(edge.to), line));

    // **Every label sits outside the OUTERMOST lane**, not beside its own:
    // a label beside an inner lane has the outer lane's line drawn straight
    // through it, which is what "2 tim|es" looked like (the owner,
    // 2026-09-14). The left margin already reserves exactly this room.
    const outermost = left - CHANNEL - (backDepth - 1) * LANE;

    parts.push(`<text class="${edge.gaveBack || !edge.self ? "rx-f-back-label" : "rx-f-label"}" ` +
      `text-anchor="end" x="${round(outermost - 4)}" ` +
      `y="${round((middle(edge.from) + middle(edge.to)) / 2 - 3)}">${escape(edge.label)}</text>`);
  }

  for (const edge of jumpEdges) {
    const x = rightOfBoxes + CHANNEL + jumpLanes.get(edge) * LANE;

    parts.push(route(x, rightOfBoxes, middle(edge.from), middle(edge.to), "rx-f-edge"));

    if (edge.label) {
      parts.push(`<text class="rx-f-label" x="${round(x + 4)}" ` +
        `y="${round((middle(edge.from) + middle(edge.to)) / 2 - 3)}">${escape(edge.label)}</text>`);
    }
  }

  nodes.forEach((node, i) => {
    const lines = node.lines.map((text, line) =>
      `<text class="${line === 0 ? "rx-f-title" : "rx-f-where"}" x="${round(left + PAD_X)}" ` +
      `y="${round(top(i) + PAD_Y + 11 + line * LINE)}">${escape(text)}</text>`).join("");

    // **The spans this box consumed, so pointing at it lights the reader's own
    // text.** That is the whole of "a flowchart based on the data": the picture
    // is about this subject, not about regular expressions in general.
    const took = node.took.map((s) => `${s.start}:${s.end}`).join(",");

    parts.push(`<g class="rx-f-box" data-start="${node.item.start}" ` +
      `data-end="${node.item.end}" data-took="${took}">` +
      `<title>${escape(`${node.fragment || "end of pattern"} - ${node.lines.join(", ")}`)}</title>` +
      `<rect class="rx-f-node" x="${round(left)}" y="${round(top(i))}" ` +
      `width="${round(boxWidth)}" height="${heights[i]}" rx="6"/>` + lines + `</g>`);
  });

  const last = bottom(nodes.length - 1);

  parts.push(`<line class="rx-f-edge" x1="${round(centre)}" y1="${round(last)}" ` +
    `x2="${round(centre)}" y2="${round(last + GAP - 6)}" marker-end="url(#rx-f-arrow-edge)"/>`);

  parts.push(`<text class="${section.matched ? "rx-f-won" : "rx-f-lost"}" ` +
    `x="${round(left)}" y="${round(last + GAP + 10)}">${escape(section.outcome)}</text>`);
}

/**
 * One routed edge: out of the boxes, along a lane, and back in at the target.
 *
 * **The corner turns BEFORE the lane, not past it.** The first version computed
 * the pre-corner point on the far side of the turn, so every rounded corner
 * overshot and hooked back on itself - inverted at both ends and on both sides,
 * which the owner reported as the corners being the wrong way round.
 */
/**
 * **One marker per line colour, chosen by the path's class.** A single shared
 * head taking the line's own stroke through `context-stroke` was tried first
 * and Safari draws nothing for it, which the owner found on the live site
 * within minutes (2026-09-14): every arrow on the page was gone. Two heads in
 * two fixed colours need no browser feature at all.
 */
function head(className) {
  return className === "rx-f-back" ? "rx-f-arrow-back" : "rx-f-arrow-edge";
}

function route(x, edge, fromY, toY, className) {
  const towards = x < edge ? 6 : -6;      // which way the lane sits from the boxes
  const up = toY < fromY ? -6 : 6;

  return `<path class="${className}" marker-end="url(#${head(className)})" d="` +
    `M ${round(edge)} ${round(fromY)} ` +
    `L ${round(x + towards)} ${round(fromY)} ` +
    `Q ${round(x)} ${round(fromY)} ${round(x)} ${round(fromY + up)} ` +
    `L ${round(x)} ${round(toY - up)} ` +
    `Q ${round(x)} ${round(toY)} ${round(x + towards)} ${round(toY)} ` +
    `L ${round(edge - towards / 3)} ${round(toY)}"/>`;
}

/**
 * A box that goes back to itself: out of the left side, round, and back in
 * below where it left.
 *
 * **This is what a repeat giving text back looks like from the pattern's side.**
 * The engine is not moving through the pattern at all - it is standing on one
 * item while the text under it slides backwards - so the arrow has to return to
 * the box it came from rather than to some earlier one.
 */
function loop(x, edge, y, className) {
  return `<path class="${className}" marker-end="url(#${head(className)})" d="` +
    `M ${round(edge)} ${round(y - 9)} ` +
    `L ${round(x + 6)} ${round(y - 9)} ` +
    `Q ${round(x)} ${round(y - 9)} ${round(x)} ${round(y - 3)} ` +
    `L ${round(x)} ${round(y + 3)} ` +
    `Q ${round(x)} ${round(y + 9)} ${round(x + 6)} ${round(y + 9)} ` +
    `L ${round(edge - 2)} ${round(y + 9)}"/>`;
}

/**
 * Give each routed edge a lane so two of them do not draw over each other. An
 * edge that spans another gets a lane further out.
 */
function laneFor(routed) {
  const lanes = new Map();

  for (const edge of routed) {
    const low = Math.min(edge.from, edge.to);
    const high = Math.max(edge.from, edge.to);

    let lane = 0;

    for (const [other, taken] of lanes) {
      const otherLow = Math.min(other.from, other.to);
      const otherHigh = Math.max(other.from, other.to);

      if (low <= otherHigh && otherLow <= high && taken >= lane) {
        lane = taken + 1;
      }
    }

    lanes.set(edge, lane);
  }

  return lanes;
}

const depthOf = (lanes) => (lanes.size === 0 ? 0 : Math.max(...lanes.values()) + 1);

/**
 * What one step consumed: the ground between it and the next step.
 *
 * The callout fires BEFORE the item is tried, so nothing reports what an item
 * matched. Where the engine is standing next says it instead - and the last
 * step of a winning attempt has no next step, so the match's own end does.
 */
function tookAt(steps, index, subject, match) {
  const next = steps[index + 1];

  if (next) {
    return next.at > steps[index].at ? subject.slice(steps[index].at, next.at) : "";
  }

  return match && match.end > steps[index].at
    ? subject.slice(steps[index].at, match.end)
    : "";
}

/**
 * What a box says about where it has been.
 *
 * **A box visited five times must not become five lines**, or the picture is
 * the step list again with a border round it. Visits that consumed nothing are
 * a list of positions on one line, because they are the same event happening
 * at different places; visits that consumed text get a line each, because the
 * text is the thing the reader came to see.
 */
function visitLines(visits) {
  if (visits.every((v) => v.took.length === 0)) {
    const shown = visits.slice(0, 6).map((v) => v.at);
    const more = visits.length - shown.length;

    return [`at ${shown.join(", ")}${more > 0 ? `, +${more} more` : ""}`];
  }

  const lines = visits.slice(0, MAX_VISITS).map((v) =>
    v.took.length === 0 ? `at ${v.at}` : `at ${v.at}, took ${quote(v.took)}`);

  if (visits.length > MAX_VISITS) {
    lines.push(`+${visits.length - MAX_VISITS} more`);
  }

  return lines;
}

/** A run of attempts that found nothing, said in one line. */
function bandText(skipped) {
  const furthest = Math.max(...skipped.map((a) => a.furthest - a.from));

  if (skipped.length === 1) {
    return `Attempt ${skipped[0].index + 1}, from position ${skipped[0].from}: `
      + (furthest > 0 ? `read ${count(furthest, "character")} and gave up` : "gave up immediately");
  }

  return `Attempts ${skipped[0].index + 1} to ${skipped[skipped.length - 1].index + 1}, `
    + `from positions ${skipped[0].from} to ${skipped[skipped.length - 1].from}: none matched, `
    + (furthest > 0 ? `the furthest read ${count(furthest, "character")}` : "each gave up immediately");
}

function summarise(attempts, matches, blocks) {
  return `The engine made ${count(attempts.length, "attempt")} and found `
    + `${count(matches.length, "match", "matches")}. `
    + `Drawn in full: ${blocks.map((b) => `attempt ${b.index + 1}`).join(", ")}.`;
}

/**
 * What a box says it is.
 *
 * **The raw item text is not a name.** PCRE2's enumeration hands back the
 * compiled pieces, so a group arrives as `(`, an alternation as `|` and a
 * quantified group's end as `)+`. Printed literally those are boxes labelled
 * with punctuation, which is what the first draft did and it read as noise. The
 * words come from `diagram.js` so the two pictures on the page call the same
 * construct the same thing.
 */
function nameOf(text) {
  const trimmed = text.replace(/\s+$/, "");

  if (trimmed === "") return "end of pattern";
  if (trimmed.startsWith("|")) return "or";

  if (trimmed.startsWith(")")) {
    const repeat = repeatOf(trimmed.slice(1));
    return repeat ? `end of group, ${repeat}` : "end of group";
  }

  const name = groupNameFor(trimmed);
  if (name) return `start of ${name}`;

  return trimmed;
}

/** A quantifier in words. Null when there is none. */
function repeatOf(quantifier) {
  const parsed = /^(\*|\+|\?|\{(\d*)(,?)(\d*)\})([+?]?)$/.exec(quantifier);

  if (!parsed) return null;

  const [, form, low, comma, high, greed] = parsed;

  const times =
    form === "*" ? "any number of times"
    : form === "+" ? "one or more times"
    : form === "?" ? "optional"
    : comma === "" ? `${low} times`
    : high === "" ? `${low} or more times`
    : `${low} to ${high} times`;

  return greed === "?" ? `${times}, as few as possible`
    : greed === "+" ? `${times}, never giving back`
    : times;
}

/**
 * Break a sentence into lines of at most `columns` characters.
 *
 * **SVG text does not wrap and does not overflow: it is cut.** So the only
 * choice is to decide the lines here, which the mono face makes exact rather
 * than approximate. A single word longer than the measure is left to overrun
 * its line, and the canvas is sized from the lines that come out, so it still
 * fits.
 */
function wrap(text, columns) {
  const lines = [];
  let line = "";

  for (const word of text.split(" ")) {
    const candidate = line === "" ? word : `${line} ${word}`;

    if (candidate.length > columns && line !== "") {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }

  if (line !== "") {
    lines.push(line);
  }

  return lines;
}

/** The reader's own text, quoted and kept short enough to sit in a box. */
function quote(text) {
  const clipped = text.length > MAX_TEXT ? `${text.slice(0, MAX_TEXT - 1)}…` : text;

  return JSON.stringify(clipped);
}

const count = (n, noun, plural) => `${n} ${n === 1 ? noun : plural ?? `${noun}s`}`;

const round = (value) => Math.round(value * 100) / 100;

const escape = (text) =>
  String(text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
